/**
 * Native shell bridge (Capacitor) — safe to import anywhere.
 *
 * Every helper is a no-op (or a sensible web fallback) outside the iOS/Android
 * app, so components can call `haptics.light()` unconditionally. Inside the
 * app, window.Capacitor is injected by the native runtime before our bundle
 * runs; the plugin packages talk to it through registerPlugin.
 */
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { StatusBar, Style as StatusBarStyle } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapApp } from '@capacitor/app';
import { Share } from '@capacitor/share';
import { Browser } from '@capacitor/browser';

export type NativePlatform = 'ios' | 'android' | 'web';

export function isNative(): boolean {
  try {
    return typeof window !== 'undefined' && Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export function nativePlatform(): NativePlatform {
  try {
    const p = Capacitor.getPlatform();
    return p === 'ios' || p === 'android' ? p : 'web';
  } catch {
    return 'web';
  }
}

/** Touch device without hover — where press feedback and haptics make sense. */
export function isTouch(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches === true;
}

const swallow = () => undefined;

/**
 * Haptics vocabulary (mirrors Apple's UIFeedbackGenerator):
 *  - selection: scrubbing through options, tab switches
 *  - light/medium/heavy: touches with increasing weight (tap, drag snap, drop)
 *  - success/warning/error: outcome of an action
 */
export const haptics = {
  selection: () => (isNative() ? Haptics.selectionChanged().catch(swallow) : Promise.resolve()),
  light: () => (isNative() ? Haptics.impact({ style: ImpactStyle.Light }).catch(swallow) : Promise.resolve()),
  medium: () => (isNative() ? Haptics.impact({ style: ImpactStyle.Medium }).catch(swallow) : Promise.resolve()),
  heavy: () => (isNative() ? Haptics.impact({ style: ImpactStyle.Heavy }).catch(swallow) : Promise.resolve()),
  success: () => (isNative() ? Haptics.notification({ type: NotificationType.Success }).catch(swallow) : Promise.resolve()),
  warning: () => (isNative() ? Haptics.notification({ type: NotificationType.Warning }).catch(swallow) : Promise.resolve()),
  error: () => (isNative() ? Haptics.notification({ type: NotificationType.Error }).catch(swallow) : Promise.resolve()),
};

export async function setStatusBarStyle(style: 'light' | 'dark'): Promise<void> {
  if (!isNative()) return;
  try {
    // "Light" content = white icons (for dark backgrounds); "Dark" = black icons.
    await StatusBar.setStyle({ style: style === 'dark' ? StatusBarStyle.Light : StatusBarStyle.Dark });
  } catch {
    /* unsupported platform */
  }
}

export async function hideSplash(): Promise<void> {
  if (!isNative()) return;
  try {
    await SplashScreen.hide({ fadeOutDuration: 200 });
  } catch {
    /* already hidden */
  }
}

/**
 * iOS: drop the Done/‹/› accessory bar that no native chat composer shows.
 * (Keyboard.setScroll is deliberately NOT used: in the plugin source it flips
 * webView.scrollView.scrollEnabled, which is the whole webview's scroll view;
 * the keyboard "jump" is handled in CSS instead — overflow:clip on the shell
 * scroller + list-scoped scrolling in the thread.)
 */
export async function configureKeyboard(): Promise<void> {
  if (nativePlatform() !== 'ios') return;
  try {
    await Keyboard.setAccessoryBarVisible({ isVisible: false });
  } catch {
    /* unsupported */
  }
}

export async function hideKeyboard(): Promise<void> {
  if (!isNative()) return;
  try {
    await Keyboard.hide();
  } catch {
    /* no keyboard */
  }
}

export interface ShareInput {
  title?: string;
  text?: string;
  url?: string;
}

/** Native share sheet in-app, Web Share API on the web. Resolves false when neither is available. */
export async function share(input: ShareInput): Promise<boolean> {
  try {
    if (isNative()) {
      await Share.share({ title: input.title, text: input.text, url: input.url, dialogTitle: input.title });
      return true;
    }
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      await navigator.share({ title: input.title, text: input.text, url: input.url });
      return true;
    }
  } catch {
    /* user dismissed */
  }
  return false;
}

/** In-app browser (SFSafariViewController) inside the app, new tab on the web. */
export async function openExternal(url: string): Promise<void> {
  if (isNative()) {
    try {
      await Browser.open({ url, presentationStyle: 'popover' });
      return;
    } catch {
      /* fall through */
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

type Unsubscribe = () => void;

/** App became active again (foreground). Web: tab became visible. */
export function onAppResume(cb: () => void): Unsubscribe {
  if (isNative()) {
    const handle = CapApp.addListener('resume', cb);
    return () => {
      handle.then((h) => h.remove()).catch(swallow);
    };
  }
  const onVis = () => {
    if (document.visibilityState === 'visible') cb();
  };
  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}

/** Android hardware back / iOS none. */
export function onBackButton(cb: (canGoBack: boolean) => void): Unsubscribe {
  if (!isNative()) return () => undefined;
  const handle = CapApp.addListener('backButton', (e) => cb(e.canGoBack));
  return () => {
    handle.then((h) => h.remove()).catch(swallow);
  };
}

/** Deep link (sweetspot://… or universal link) opened the app. */
export function onAppUrlOpen(cb: (url: string) => void): Unsubscribe {
  if (!isNative()) return () => undefined;
  const handle = CapApp.addListener('appUrlOpen', (e) => cb(e.url));
  return () => {
    handle.then((h) => h.remove()).catch(swallow);
  };
}

/** Keyboard show/hide with height — drives --keyboard-height and data-keyboard. */
export function onKeyboard(cb: (open: boolean, height: number) => void): Unsubscribe {
  if (!isNative()) return () => undefined;
  const show = Keyboard.addListener('keyboardWillShow', (info) => cb(true, info.keyboardHeight));
  const hide = Keyboard.addListener('keyboardWillHide', () => cb(false, 0));
  return () => {
    show.then((h) => h.remove()).catch(swallow);
    hide.then((h) => h.remove()).catch(swallow);
  };
}
